import { Carrier } from '@insurgrid/common';
import { useParams, useRouter } from 'next/navigation';
import { useNavUtils } from '../../navigationHooks/useNavUtils';
import { SubmissionCreatePaths, SUBMISSION_ROOT_PATH } from '../constants';

export const useSubmissionNav = () => {
  const { push } = useRouter();
  const { generatePath } = useNavUtils();
  const params = useParams();
  const carrier = params?.carrierSlug;

  const makeCarrierNav = (navPath: string) => {
    const path = (carrierSlug?: string) =>
      generatePath(navPath, {
        carrierSlug: carrierSlug ?? carrier ?? 'travelers',
      });

    return {
      go: (carrierSlug?: string) => push(path(carrierSlug)),
      path,
    };
  };

  const validate = makeCarrierNav(SubmissionCreatePaths.VALIDATE);
  const interstitial = makeCarrierNav(
    SubmissionCreatePaths.CARRIER_INTERSTITIAL,
  );
  const upload = makeCarrierNav(SubmissionCreatePaths.UPLOAD);
  const hasAccount = makeCarrierNav(SubmissionCreatePaths.HAS_ACCOUNT);
  const makeAccount = makeCarrierNav(SubmissionCreatePaths.CREATE_ACCOUNT);
  const carrierUnavailable = makeCarrierNav(
    SubmissionCreatePaths.CARRIER_UNAVAILABLE,
  );
  const connected = makeCarrierNav(SubmissionCreatePaths.CARRIER_CONNECTED);

  const goToNextCarrierPath = (carrier: Carrier) => {
    if (carrier.manualSubmissionOnly) {
      upload.go(carrier.slug);
    } else if (carrier.hasChildren) {
      interstitial.go(carrier.slug);
    } else {
      validate.go(carrier.slug);
    }
  };

  const intakeFormPath = generatePath(SubmissionCreatePaths.INTAKE_FORM);

  const intakeForm = {
    go: () => push(intakeFormPath),
    path: intakeFormPath,
  };

  const carrierSelectPath = generatePath(SubmissionCreatePaths.SELECT_CARRIER);

  const carrierSelect = {
    go: () => {
      push(carrierSelectPath);
    },
    path: carrierSelectPath,
  };

  const fetchingPath = generatePath(SubmissionCreatePaths.FETCHING);

  const beagleOfferPath = generatePath(SubmissionCreatePaths.BEAGLE_OFFER);
  const beagleThanksPath = generatePath(SubmissionCreatePaths.BEAGLE_THANKS);
  const beaglePropertyManagerPath = generatePath(
    SubmissionCreatePaths.BEAGLE_PROPERTY_MANAGER,
  );

  const overviewPath = generatePath(SUBMISSION_ROOT_PATH);

  const overview = {
    go: () => {
      push(overviewPath);
    },
    path: overviewPath,
  };

  const fetching = {
    go: () => {
      push(fetchingPath);
    },
    path: fetchingPath,
  };

  const beagleOffer = {
    go: () => {
      push(beagleOfferPath);
    },
    path: beagleOfferPath,
  };

  const beagleThanks = {
    go: () => {
      push(beagleThanksPath);
    },
    path: beagleThanksPath,
  };

  const beaglePropertyManager = {
    go: () => {
      push(beaglePropertyManagerPath);
    },
    path: beaglePropertyManagerPath,
  };

  const additionalCarriersPath = generatePath(
    SubmissionCreatePaths.ADDITIONAL_CARRIER,
  );

  const additionalCarriers = {
    go: () => {
      push(additionalCarriersPath);
    },
    path: carrierSelectPath,
  };

  const getContinueSubmissionPath = (slug: string) =>
    generatePath(SubmissionCreatePaths.CONTINUE_EXISTING, {
      carrierSlug: slug,
    });

  const continueSubmission = {
    go: (carrierSlug: string) => {
      push(getContinueSubmissionPath(carrierSlug));
    },
  };

  return {
    intakeForm,
    carrierSelect,
    upload,
    validate,
    interstitial,
    fetching,
    hasAccount,
    makeAccount,
    goToNextCarrierPath,
    additionalCarriers,
    continueSubmission,
    overview,
    carrierUnavailable,
    connected,
    beagleOffer,
    beagleThanks,
    beaglePropertyManager,
  };
};
